import { useEffect, useState } from "react";
import { ChevronDown, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import LinkedText from "@/components/LinkedText";
import useSEO from "@/lib/useSEO";
import api from "@/lib/api";

const SEED_FAQS = [
  { question: "Do you charge by the hour?", answer: "No. We price per job because every home or business is different." },
  { question: "Are the prices final?", answer: "No. Prices are ballpark estimates only. Final pricing may change after inspection or review of photos." },
];

export default function Faq() {
  useSEO({ slug: "faq", title: "Cleaning Service FAQ | P & A Cleaning Sacramento" });

  const [faqs, setFaqs] = useState(SEED_FAQS);
  const [open, setOpen] = useState(0);

  useEffect(() => {
    api.get("/public/faqs").then((r) => Array.isArray(r.data) && r.data.length > 0 && setFaqs(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="page-faq">
      <section className="bg-gradient-to-b from-[#F8FAFC] to-white py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-[#1E3A8A] shadow-soft">
            <Sparkles className="w-3.5 h-3.5 text-[#34D399]" /> Helpful info
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl font-display font-bold text-[#0F172A] tracking-tight">
            Frequently asked <span className="gradient-text">questions</span>
          </h1>
          <p className="mt-4 text-[#475569] text-lg">
            Quick answers about pricing, scheduling, and how we work.
          </p>
        </div>
      </section>

      <section className="pb-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3">
          {faqs.map((f, idx) => {
            const isOpen = open === idx;
            return (
              <div key={f.id || idx} data-testid={`faq-item-${idx}`} className="bg-[#F8FAFC] border border-slate-100 rounded-2xl overflow-hidden">
                <button
                  className="w-full flex items-center justify-between text-left px-6 py-4 hover:bg-slate-50 transition-colors"
                  onClick={() => setOpen(isOpen ? -1 : idx)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${idx}`}
                  data-testid={`faq-toggle-${idx}`}
                >
                  <span className="font-display font-semibold text-[#0F172A] text-base">{f.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#1E3A8A] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {/* Always in the HTML (crawlable links); hidden until opened. */}
                <div
                  id={`faq-answer-${idx}`}
                  hidden={!isOpen}
                  data-testid={`faq-answer-${idx}`}
                  className="px-6 pb-5 text-[#475569] leading-relaxed text-sm"
                >
                  <LinkedText as="div" text={f.answer} className="whitespace-pre-line" />
                </div>
              </div>
            );
          })}
          <div className="mt-10 text-center">
            <Link to="/request-estimate" data-testid="faq-cta-estimate" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF]">
              Request an Estimate
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
